import React from 'react'
import PropTypes from 'prop-types'
import { Grid, Dialog, DialogContent, DialogActions, DialogContentText, Button, DialogTitle } from '@material-ui/core'
import MaterialInputable from './MaterialInputable'
import MaterialSelectable from './MaterialSelectable'
import Address from './Address'
import DealerLocation from './DealerLocation'
import FormValidator from './FormValidator'
import api from '../api'

class NewAccountDialog extends React.Component {
	constructor(props) {
		super(props)

		this.addressComponent = React.createRef()

		this.validator = new FormValidator([
			{
				field: 'accountNumber',
				method: 'isEmpty',
				validWhen: false,
				message: 'Fleet Store Account Number is required'
			},
			{
				field: 'accountNumber',
				method: this.accountExists,
				validWhen: false,
				message: 'Fleet Store Account Number must be unique per Dealer'
			},
			{
				field: 'accountName',
				method: 'isEmpty',
				validWhen: false,
				message: 'Customer Name is required'
			},
			{
				field: 'dealerLocation',
				method: 'isEmpty',
				validWhen: false,
				message: 'Dealer is required'
			},
			{
				field: 'dealerLocation',
				method: this.dealerLocationExists,
				validWhen: true,
				message: 'Dealer is invalid'
			},
		])

		this.state = {
			accountNumber: '',
			accountName: '',
			dealerLocation: '',
			dealerLocationId: -1,
			address: {
				address: '',
				address2: '',
				city: '',
				state: '',
				zip: '',
				countryId: 'USA'
			},
			validation: this.validator.valid()
		}

		this.onClose = this.onClose.bind(this)
		this.validate = this.validate.bind(this)
		this.dealerLocationExists = this.dealerLocationExists.bind(this)
		this.updateAccountNumber = this.updateAccountNumber.bind(this)
		this.updateAccountName = this.updateAccountName.bind(this)
		this.updateDealerLocation = this.updateDealerLocation.bind(this)
		this.updateCountry = this.updateCountry.bind(this)
		this.updateAddress = this.updateAddress.bind(this)
		this.setCountry = this.setCountry.bind(this)
	}

	async accountExists(accountNumber, state) {
		const accountResponse = await api.service('localAccounts').find({
			query: { 
				account_number: { $eq: accountNumber },
				dealer_location_id: { $eq: state.dealerLocationId }
			}
		})

		return (accountResponse.total > 0)
	}

	dealerLocationExists(dealerLocation, state) {
		return (state.dealerLocationId > 0)
	}

	updateAccountNumber(event) {
		let validation = Object.assign({}, this.state.validation)
		validation['accountNumber'].isInvalid = false
		validation['accountNumber'].message = ''

		this.setState({ 
			accountNumber: event.target.value,
			validation: validation 
		})
	}

	updateAccountName(event) {
		let validation = Object.assign({}, this.state.validation)
		validation['accountName'].isInvalid = false
		validation['accountName'].message = ''

		this.setState({
			accountName: event.target.value,
			validation: validation
		})
	}

	updateDealerLocation(event, id) {
		let validation = Object.assign({}, this.state.validation)
		validation['dealerLocation'].isInvalid = false
		validation['dealerLocation'].message = ''

		this.setState({
			dealerLocation: event.target.value,
			dealerLocationId: id,
			validation: validation
		})
	}

	updateCountry(event) {
		this.setCountry(event.target.value)
	}

	updateAddress(event) {
		let address = Object.assign({}, this.state.address)

		const name = event.target.name
		const value = event.target.value

		address[name] = value

		this.setState({ address: address })
	}

	setCountry(countryId) {
		if (countryId) {
			let address = Object.assign({}, this.state.address)

			address.countryId = countryId
			this.setState({ address: address })
		}
	}

	/**
	 * Performs validation of the data in the dialog.
	 * @param {event} event - the calling event
	 * @returns {boolean} - is the form valid?
	 * @public
	 */
	async validate(event) {
		const validation = await this.validator.validate(this.state, this.props)
		this.setState({ validation })

		const addressIsValid = this.addressComponent.current.isValid()

		if (validation.isValid && addressIsValid) {
			const { accountNumber, accountName, dealerLocationId, address } = this.state
			
			this.props.onNew(accountNumber, accountName, dealerLocationId, address)

			this.clearState()
		}

		return validation.isValid
	}
	
	clearState() {
		let address = Object.assign({}, this.state.address)

		Object.keys(address).forEach(key => {
			address[key] = ''
		})
		address.countryId = 'USA'

		this.setState({ 
			accountNumber: '',
			accountName: '', 
			dealerLocation: '',
			dealerLocationId: -1,
			address: address,
			validation: this.validator.valid()
		})
	}

	onClose() {
		this.clearState()

		if(this.props.onClose)
			this.props.onClose()
	}
	
	render() {
		const {accountName, accountNumber, dealerLocation, address, validation} = this.state
		const {countryTypeOptions} = this.props

		return (
			<Dialog
				open={this.props.open}
				onClose={this.props.onClose}
				aria-labelledby="form-dialog-title"
				maxWidth='md'
			>
				<DialogTitle>Create new LBB Account</DialogTitle>

				<DialogContent>
					<DialogContentText>
						Please enter the information for the new account.
					</DialogContentText>

					<Grid container spacing={1}>
						<Grid item xs={12} md={4}>
							<MaterialInputable
								autoFocus
								required
								name="accountNumber"
								label="Fleet Store Account Number"
								value={accountNumber}
								error={validation.accountNumber.isInvalid}
								errorMessage={validation.accountNumber.message} 
								onChange={event => this.updateAccountNumber(event)} />
						</Grid>

						<Grid item xs={12} md={6}>
							<MaterialInputable
								required
								name="accountName"
								label="Customer Name"
								value={accountName}
								error={validation.accountName.isInvalid}
								errorMessage={validation.accountName.message}
								onChange={event => this.updateAccountName(event)} />
						</Grid>

						<Grid item xs={12} md={2}>
							<MaterialSelectable
								required
								value={address.countryId}
								name="country"
								label="Country"
								onChange={this.updateCountry}
								options={countryTypeOptions} />
						</Grid>

						<Grid item xs={12}>
							<DealerLocation
								required
								name="dealerLocation"
								label="Dealer"
								value={dealerLocation}
								error={validation.dealerLocation.isInvalid}
								errorMessage={validation.dealerLocation.message}
								onChange={(event, id) => this.updateDealerLocation(event, id)} />
						</Grid>

						<Grid item xs={12}>
							<Address 
								required
								extendedAddress
								values={address} 
								ref={this.addressComponent}
								onChange={event => this.updateAddress(event)} 
								onSelectChange={event => this.updateAddress(event)} />
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions>
					<Button onClick={this.validate} color="secondary">Save</Button>
					<Button onClick={this.onClose} color="primary">Exit</Button>
				</DialogActions>
			</Dialog>
		)
	}
}

NewAccountDialog.propTypes = {
	classes: PropTypes.object,
	country: PropTypes.string,
	countryTypeOptions: PropTypes.array,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func,
	onNew: PropTypes.func,
}

export default NewAccountDialog


// WEBPACK FOOTER //
// ./src/components/NewAccountDialog.js